import { useState, useEffect, useCallback, useRef } from 'react';
import { frameOrderApi } from '../services/frameOrderApi';
import type {
    FrameOrder,
    FrameOrderFilterOptions,
    FrameOrderFilters,
    FrameOrderAggregateStats,
    SortField,
    SortOrder,
} from '../types/frameOrders';

interface UseFrameOrdersReturn {
    orders: FrameOrder[];
    total: number;
    stats: FrameOrderAggregateStats;
    loading: boolean;
    error: string | null;
    filters: FrameOrderFilters;
    filterOptions: FrameOrderFilterOptions | null;
    sortBy: SortField;
    sortOrder: SortOrder;
    page: number;
    limit: number;
    setFilters: (filters: FrameOrderFilters) => void;
    setSort: (field: SortField) => void;
    setPage: (page: number) => void;
    refresh: () => void;
}

export function useFrameOrders(): UseFrameOrdersReturn {
    const defaultStats: FrameOrderAggregateStats = { totalOutstandingQty: 0, greenCount: 0, yellowCount: 0, redCount: 0, unknownCount: 0 };
    const [orders, setOrders] = useState<FrameOrder[]>([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState<FrameOrderAggregateStats>(defaultStats);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFiltersState] = useState<FrameOrderFilters>({ documentType: 'FrameOrder' });
    const [filterOptions, setFilterOptions] = useState<FrameOrderFilterOptions | null>(null);
    const [sortBy, setSortBy] = useState<SortField>('postingOrOrderDate');
    const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
    const [page, setPage] = useState(1);
    const limit = 50;
    const refreshCounter = useRef(0);

    // Re-fetch filter options when relevant filters change (for dependent dropdowns)
    useEffect(() => {
        frameOrderApi.getFilters(filters)
            .then(setFilterOptions)
            .catch(err => console.error('Failed to load filter options:', err));
    }, [filters.companyCode, filters.documentType, filters.classificationCode, filters.salesTeam]);

    // Load data when filters/sort/page change
    useEffect(() => {
        let cancelled = false;
        const currentRefresh = ++refreshCounter.current;

        setLoading(true);
        setError(null);

        frameOrderApi.list({ page, limit, sortBy, sortOrder, filters })
            .then(result => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setOrders(result.items);
                    setTotal(result.total);
                    setStats(result.stats);
                }
            })
            .catch(err => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setError(err.message);
                }
            })
            .finally(() => {
                if (!cancelled && currentRefresh === refreshCounter.current) {
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [filters, sortBy, sortOrder, page]);

    const setFilters = useCallback((newFilters: FrameOrderFilters) => {
        setFiltersState(newFilters);
        setPage(1); // Reset to first page on filter change
    }, []);

    const setSort = useCallback((field: SortField) => {
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
            } else {
                setSortOrder('DESC');
            }
            return field;
        });
        setPage(1);
    }, []);

    const refresh = useCallback(() => {
        refreshCounter.current++;
        setLoading(true);
        setError(null);

        frameOrderApi.list({ page, limit, sortBy, sortOrder, filters })
            .then(result => {
                setOrders(result.items);
                setTotal(result.total);
                setStats(result.stats);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [page, sortBy, sortOrder, filters]);

    return {
        orders, total, stats, loading, error,
        filters, filterOptions,
        sortBy, sortOrder, page, limit,
        setFilters, setSort, setPage, refresh,
    };
}
